import { CaretDownFilled, SettingOutlined, CloseCircleFilled } from '@ant-design/icons'
import { unwrapResult } from '@reduxjs/toolkit'
import {
  Col,
  Form,
  FormProps,
  Input,
  Radio,
  Row,
  Select,
  Cascader as Cascaded,
} from 'antd'
import moment from 'moment'
import React, { ReactElement, useContext, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootDispatch } from '../../../app/store'
import { getMaritalStatusName, MaritalStatus } from '../../../models/patient'
import { Gender, GenderOptions, getGenderName } from '../../../models/user'
import { ThemeContext } from '../../../theme/ThemeContext'
import { getAddress } from '../../registration/register/registerSlice'
import styles from './PatientForm.module.css'
import { getPatientOptionsAsync } from '../../A-toothModule/toothHome/toothHomeSlice'
import { SettingOptionsModal } from '../../A-toothModule/toothHome/modal/settingOptionsModal'
import { phonePatterns } from '../../../utils/RegExp'
import { selectEdition } from '../../../app/applicationSlice'
import { getFieldSettingList } from '../../FunctionSetting/functionSettingPage/fieldSetting/FieldSettingSlice'
import {NATION_LIST} from "../../../models/nation";

interface RegistrationsProps {
  registrations?: any
  form?: any
  CKHZSJHData?: any
  patients?: any
}

export const PatientForm = ({
  registrations,
  form,
  CKHZSJHData,
  patients,
  ...others
}: RegistrationsProps & FormProps): ReactElement => {
  const hisVersion = useSelector(selectEdition)

  const dispatch = useDispatch<RootDispatch>()

  const theme = useContext(ThemeContext)

  const [fieldSettingList, setFieldSettingList] = useState<any[]>([]) //字段必填设置

  const [phoneInfo, setPhoneInfo] = useState<any>({})

  useEffect(() => {
      setPhoneInfo({
        phone: patients?.phone,
        emergencyContactPhone: patients?.emergencyContactPhone,
        phoneDisabled: patients?.phone && !CKHZSJHData,
        emergencyContactPhoneDisabled: patients?.emergencyContactPhone && !CKHZSJHData,
        phoneHover: false,
        emergencyContactPhoneHover: false,
      })
  }, [patients,CKHZSJHData])

  useEffect(() => {
    dispatch(getFieldSettingList({
      functionType: 2,
      templateType: hisVersion == 1 ? 'KQHZSZ' : 'HZSZ',
    }))
      .then(unwrapResult)
      .then((res: any) => {
        const data = res?.[0]?.fields || []
        setFieldSettingList(data);
      })
  }, [])

  const layouts = {
    labelCol: { span: 2 },
    wrapperCol: { span: 22 },
  }

  const layoutSecond = {
    labelCol: { span: 3 },
    wrapperCol: { span: 21 },
  }
  const [idCardRules, setIdCardRules] = useState<any[]>([
    {
      pattern: /(^\d{15}$)|(^\d{16}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/,
      message: '你的身份证格式不正确',
    },
    {
      max: 18,
      message: '最长18位!',
    },
  ])

  const getIdCardInfo = (idCard: string) => {
    if (idCard) {
      //先判断是不是中国大陆身份证
      if (
        (idCard.length != 15 && idCard.length != 16 && idCard.length != 18) ||
        isNaN(Number(idCard.substr(0, 6)))
      ) {
        setIdCardRules([
          {
            max: 18,
            message: '最长18位!',
          },
        ])
      } else {
        setIdCardRules([
          {
            pattern: /(^\d{15}$)|(^\d{16}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/,
            message: '身份证格式错误',
          },
          {
            max: 18,
            message: '最长18位!',
          },
        ])
      }
    }
  }

  const updateBirthday = () => {
    if (form) {
      const year = parseInt(form.getFieldValue('ageYear')) || 0
      const month = parseInt(form.getFieldValue('ageMonth')) || 0
      if (year !== 0 || month !== 0) {
        const months = year * 12 + month
        const birthday = moment()
          .subtract(months, 'months')
          .format('YYYY/MM/DD')
        form.setFieldsValue({ birthday })
      }
    }
  }

  const updateYearsAndMonths = () => {
    if (form) {
      const date = moment(form.getFieldValue('birthday'))
      if (!date.isValid()) {
        return
      }
      const duration = moment.duration(moment().diff(date))
      if (duration.years() < 0 || duration.months() < 0) {
        return
      }
      form.setFieldsValue({
        ageYear: duration.years(),
        ageMonth: duration.months() == 0 ? 1 : duration.months(),
      })
    }
  }

  const [allList, setAllList] = useState<any[]>() //省市数据

  const [classificationList, setClassificationList] = useState<any>() //自定义患者来源数据
  
  const [settingOptionsVisible, setSettingOptionsVisible] = useState(false) //自定义选项

  const [settingType, setSettingType] = useState('0') // 自定义类型

  useEffect(() => {
    //获取省市数据
    dispatch(getAddress())
      .then(unwrapResult)
      .then((res: any) => {
        setAllList(res)
      })
  }, [])

  useEffect(() => {
    if(!settingOptionsVisible) {
      dispatch(getPatientOptionsAsync({ types: '0,1,2' }))
        .then(unwrapResult)
        .then((res: any) => {
          const findItems = (type: string) => {
            return res ? res?.find((t: any) => t.type == type)?.items?.map((v: any) => {
              return {
                name: v.context,
                value: v.sortNo,
              }
            }) : []
          }
          setClassificationList({
            sourceList: findItems('1'),
          })
        })
    }
  }, [settingOptionsVisible])

  return (
    <Form
      form={form}
      className={styles.form}
      autoComplete='off'
      colon={false}
      initialValues={{
        sex: Gender.Male,
      }}
      {...others}
      labelCol={{ span: 6 }}
      wrapperCol={{ span: 18 }}
    >
      <Row>
        <Col span={8} className={styles.fst}>
          <Form.Item
            name='name'
            label='患者姓名'
            rules={[{ required: true }, { max: 40, message: '最长40位' }]}
          >
            <Input
              disabled={
                registrations?.insuranceCode &&
                registrations?.insuranceCode != 'ZIFEI'
                  ? true
                  : false
              }
            />
          </Form.Item>
        </Col>
        <Col span={8} className={styles.sex}>
          <Form.Item name='sex' label='性别' rules={[{ required: true }]}>
            <Radio.Group
              disabled={
                registrations?.insuranceCode &&
                registrations?.insuranceCode != 'ZIFEI'
                  ? true
                  : false
              }
            >
              {GenderOptions.map((g) => (
                <Radio key={g} value={g}>
                  {getGenderName(g)}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label='年龄'
            style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
            }}
            required
            className={styles.snd}
          >
            <Row wrap={false} align='middle'>
              <Form.Item
                name='ageYear'
                noStyle
                dependencies={['ageMonth']}
                rules={[
                  {
                    max: 4,
                    pattern: /^\d*$/,
                    message: '请输入正确的年龄',
                  },

                  ({ getFieldValue }) => ({
                    validator: (_, value) => {
                      if (!value && !getFieldValue('ageMonth')) {
                        return Promise.reject(new Error('请输入正确的年龄'))
                      }
                      return Promise.resolve()
                    },
                  }),
                ]}
              >
                <Input
                  disabled={
                    registrations?.insuranceCode &&
                    registrations?.insuranceCode != 'ZIFEI'
                      ? true
                      : false
                  }
                  style={{ width: '5rem', textAlign: 'center' }}
                  onChange={updateBirthday}
                />
              </Form.Item>
              <div
                style={{
                  color: theme.tc2,
                  fontSize: '1.125rem',
                  margin: '0 10px',
                }}
              >
                岁
              </div>
              <Form.Item
                name='ageMonth'
                noStyle
                rules={[
                  {
                    max: 2,
                    pattern: /^(0?[0-9]|1[0-1])$/,
                    message: '请输入正确的月份',
                  },
                  ({ getFieldValue }) => ({
                    validator: (_, value) => {
                      if (!value && !getFieldValue('ageYear')) {
                        return Promise.reject(new Error('请输入正确的月份'))
                      }
                      return Promise.resolve()
                    },
                  }),
                ]}
              >
                <Input
                  disabled={
                    registrations?.insuranceCode &&
                    registrations?.insuranceCode != 'ZIFEI'
                      ? true
                      : false
                  }
                  style={{ width: '5rem', textAlign: 'center' }}
                  onChange={updateBirthday}
                />
              </Form.Item>
              <div
                style={{
                  color: theme.tc2,
                  fontSize: '1.125rem',
                  margin: '0 10px',
                }}
              >
                月
              </div>
              <Form.Item name='birthday' noStyle style={{ flex: 1 }}>
                <Input
                  onChange={updateYearsAndMonths}
                  disabled={
                    registrations?.insuranceCode &&
                    registrations?.insuranceCode != 'ZIFEI'
                      ? true
                      : false
                  }
                />
              </Form.Item>
            </Row>
          </Form.Item>
        </Col>
        <Col span={8} className={styles.fst}>
          <Form.Item
            label='身高'
            name='height'
            rules={[
              { required: fieldSettingList?.find((item) => item.key == 'height')?.isRequired ,
                message: "请输入身高"
              },
              {
                message: "请输入最多带两位小数的数值",
                pattern: new RegExp(/^[0-9]+((\.)[0-9]{0,2})?$/),
              },
            ]}
          >
            <Input suffix='cm' />
          </Form.Item>
        </Col>
        <Col span={8} className={styles.fst}>
          <Form.Item
            label='体重'
            name='weight'
            rules={[
              { required: fieldSettingList?.find((item) => item.key == 'weight')?.isRequired ,
                message: "请输入体重"
              },
              {
                message: '请输入最多带两位小数的数值',
                pattern: new RegExp(/^[0-9]+((\.)[0-9]{0,2})?$/),
              },
            ]}
          >
            <Input suffix='kg' />
          </Form.Item>
        </Col>
        <Col span={8} className={styles.snd}>
          <Form.Item label='腰围' name='waistline' 
            rules={[
              { required: fieldSettingList?.find((item) => item.key == 'waistline')?.isRequired ,
                message: "请输入腰围"
              },
              {
                message: '请输入最多带两位小数的数值',
                pattern: new RegExp(/^[0-9]+((\.)[0-9]{0,2})?$/),
              },
            ]}>
            <Input suffix='cm'/>
          </Form.Item>
        </Col>
        <Col span={8} className={styles.fst}>
          <Form.Item
            label='婚姻状况'
            name='maritalStatus'
            initialValue={MaritalStatus.Unknown}
            rules={[
              { required: fieldSettingList?.find((item) => item.key == 'maritalStatus')?.isRequired ,
                message: "请输入婚姻状况"
              },
            ]}
          >
            <Select>
              {[
                MaritalStatus.Unknown,
                MaritalStatus.Single,
                MaritalStatus.Married,
              ].map((s) => (
                <Select.Option key={s} value={s}>
                  {getMaritalStatusName(s)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={8} className={styles.fst}>
          <Form.Item
            label='邮箱地址'
            name='emailAddress'
            rules={[
              { required: fieldSettingList?.find((item) => item.key == 'emailAddress')?.isRequired ,
                message: "请输入邮箱地址"
              },
              {
                max: 50,
                message: '最长50位!',
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={8} className={styles.snd}>
          <Form.Item
            label="微信号"
            name="wechat"
            rules={[
              {
                required: fieldSettingList?.find((item) => item.key == 'wechat')?.isRequired,
                message: "请输入微信号"
              },
            ]}>
            <Input />
          </Form.Item>
        </Col>
        <Col span={8} className={styles.fst}>
          <Form.Item
            name='phone'
            label='联系方式'
            rules={[
              phoneInfo?.phoneDisabled ? {} : { ...phonePatterns },
              {
                required: fieldSettingList?.find((item) => item.key == 'phone')?.isRequired,
                message: "请输入联系方式"
              },
            ]}
          >
            <Input 
              maxLength={11} 
              disabled={phoneInfo?.phoneDisabled} 
              suffix={
                phoneInfo?.phoneDisabled ? (
                  <CloseCircleFilled 
                    onClick={() => {
                      form.setFieldsValue({ phone: '' })
                      setPhoneInfo({
                        ...phoneInfo,
                        phone: '',
                        phoneDisabled: false,
                      })
                    }} 
                    style={{ color: '#999', cursor: 'pointer' }} 
                  />
                ) : null
              }
            />
          </Form.Item>
        </Col>
        <Col span={8} className={styles.fst}>
          <Form.Item
            name="idCardNumber"
            label="身份证号"
            rules={[
              ...idCardRules,
              {
                required: fieldSettingList?.find((item) => item.key == 'idCardNumber')?.isRequired,
                message: "请输入身份证号"
              },
            ]}>
            <Input
              disabled={
                registrations?.insuranceCode &&
                registrations?.insuranceCode != 'ZIFEI'
                  ? true
                  : false
              }
              onChange={(e: any) => {
                getIdCardInfo(e.target.value)
              }}
            />
          </Form.Item>
        </Col>
        <Col span={8} className={styles.snd}>
          <Form.Item
            name='companyName'
            label='工作单位'
            rules={[
              {
                required: fieldSettingList?.find((item) => item.key == 'companyName')?.isRequired,
                message: "请输入工作单位"
              },
              {
                max: 120,
                message: '最长120位!',
              },
            ]}
          >
            <Input
              maxLength={120}
              disabled={
                registrations?.insuranceCode &&
                registrations?.insuranceCode != 'ZIFEI'
                  ? true
                  : false
              }
            />
          </Form.Item>
        </Col>
        <Col span={8} className={styles.fst}>
          <Form.Item
            name="area"
            label="家庭地区"
            rules={[
              {
                required: fieldSettingList?.find((item) => item.key == 'area')?.isRequired,
                message: "请选择家庭地区"
              },
            ]}
          >
            <Cascaded
              allowClear={false}
              // loadData={loadData}
              options={allList}
              placeholder='请选择'
            />
          </Form.Item>
        </Col>
        <Col span={16} className={styles.fst}>
          <Form.Item
            {...layoutSecond}
            name='address'
            label='详细地址'
            rules={[
              {
                required: fieldSettingList?.find((item) => item.key == 'address')?.isRequired,
                message: "请输入详细地址"
              },
              {
                max: 250,
                message: '最长250位!',
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
        {/* <Col span={24} className={styles.fst}>
          <Form.Item
            name='address'
            label='地址'
            rules={[
              {
                max: 250,
                message: '最长250位!',
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Col> */}
        <Col span={8} className={styles.fst}>
          <Form.Item
            name='emergencyContactName'
            label='紧急联系人'
            rules={[
              {
                required: fieldSettingList?.find((item) => item.key == 'emergencyContactName')?.isRequired,
                message: "请输入紧急联系人"
              },
              {
                max: 40,
                message: '最长40位!',
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={8} className={styles.fst}>
          <Form.Item
            name='emergencyContactPhone'
            label='紧急联系方式'
            rules={[
              phoneInfo?.emergencyContactPhoneDisabled ? {} : { ...phonePatterns },
              {
                required: fieldSettingList?.find((item) => item.key == 'emergencyContactPhone')?.isRequired,
                message: "请输入紧急联系方式"
              },
              {
                max: 40,
                message: '最长40位!',
              },
            ]}
          >
            <Input 
              disabled={phoneInfo?.emergencyContactPhoneDisabled} 
              suffix={
                phoneInfo?.emergencyContactPhoneDisabled ? (
                  <CloseCircleFilled 
                    onClick={() => {
                      form.setFieldsValue({ emergencyContactPhone: '' })
                      setPhoneInfo({
                        ...phoneInfo,
                        emergencyContactPhone: '',
                        emergencyContactPhoneDisabled: false,
                      })
                    }} 
                    style={{ color: '#999', cursor: 'pointer' }} 
                  />
                ) : null
              }
            />
          </Form.Item>
        </Col>
        <Col span={8} className={styles.snd}>
          <Form.Item
            label="患者来源"
            name="patientSource"
            rules={[
              {
                required: fieldSettingList?.find((item) => item.key == 'patientSource')?.isRequired,
                message: "请选择患者来源"
              },
            ]}
          >
            <Select>
              {classificationList?.sourceList.map((v: any) => {
                return (
                  <Select.Option key={v.name} value={v.name}>
                    {v.name}
                  </Select.Option>
                )
              })}
            </Select>
          </Form.Item>
          <div style={{ position: 'absolute', top: '9px', right: '-20px', zIndex: 999 }}>
            <SettingOutlined
              style={{ color: '#999999', fontSize: '16px' }}
              onClick={() => {
                setSettingOptionsVisible(true)
                setSettingType('1')
              }}
            />
          </div>
        </Col>
        <Col span={8} className={styles.fst}>
          <Form.Item
            name='nativePlace'
            label='籍贯'
            rules={[
              {
                required: fieldSettingList?.find((item) => item.key == 'nativePlace')?.isRequired,
                message: "请输入籍贯"
              },
              {
                max: 40,
                message: '最长40位!',
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={8} className={styles.snd}>
          <Form.Item
            label="民族"
            name="nation"
            rules={[
              {
                required: fieldSettingList?.find((item) => item.key == 'nation')?.isRequired,
                message: "请选择民族"
              },
            ]}
          >
              <Select
                  allowClear
                  placeholder='请选择民族'
              >
                  {NATION_LIST.map((nation) => (
                      <Select.Option key={nation} value={nation}>
                          {nation}
                      </Select.Option>
                  ))}
              </Select>
          </Form.Item>
        </Col>
        <Col span={8} className={styles.snd}>
          <Form.Item
            label='职业'
            name='job'
            rules={[
              {
                required: fieldSettingList?.find((item) => item.key == 'job')?.isRequired,
                message: "请输入职业"
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={24} className={styles.fst}>
          <Form.Item
            {...layouts}
            name='remark'
            label='备注'
            rules={[
              {
                max: 250,
                message: '最长250位!',
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <SettingOptionsModal
        visible={settingOptionsVisible}
        type={settingType}
        onCancel={() => {
          setSettingOptionsVisible(false)
        }}
        onOk={() => {
          setSettingOptionsVisible(false)
        }}
      />

    </Form>
  )
}
